/**
 * Crowd frame driver (P1-T11): picks who is out (hour gate), who is near
 * (route-bbox grid), sorts by distance, and submits to the VAT pools —
 * nearest to mid LOD, the rest to far impostor-quality LOD.
 */
import { CrowdField, type CrowdLook } from './crowd';
import {
  buildCitizens,
  buildRoutes,
  citizenPose,
  isOut,
  CitizenGrid,
  type Citizen,
  type Pose,
  type Route,
} from './routes';

export interface CrowdDriverOptions {
  tier: 'low' | 'mid' | 'high';
  /** max visible people (tier budget) */
  visible: number;
  /** mid-LOD radius; beyond it up to `radius` goes far */
  midRadius: number;
  radius: number;
  /** total simulated citizens (cheap; positions are closed-form) */
  population: number;
  /** 0 disables the crowd entirely (feature flag) */
  enabled: boolean;
}

const MID_R: Record<'low' | 'mid' | 'high', number> = { low: 34, mid: 60, high: 85 };

export class CrowdDriver {
  private readonly routes: Route[];
  private readonly citizens: Citizen[];
  private readonly grid: CitizenGrid;
  private readonly pose: Pose = { x: 0, z: 0, yaw: 0 };
  private readonly candKey = { k: '' };
  private cand: number[] = [];
  private visibleCount = 0;
  /** sim clock seconds (real time, not the 30x world clock) */
  private t = 0;

  constructor(
    private readonly field: CrowdField,
    private readonly opts: CrowdDriverOptions,
  ) {
    this.routes = buildRoutes();
    this.citizens = buildCitizens(opts.population, this.routes);
    this.grid = new CitizenGrid(this.citizens, this.routes);
  }

  get population(): number {
    return this.citizens.length;
  }

  get routesCount(): number {
    return this.routes.length;
  }

  update(dtMs: number, camX: number, camZ: number, hourOfDay: number, day: number, rain: number): void {
    if (!this.opts.enabled) {
      this.field.begin(dtMs);
      this.visibleCount = this.field.end();
      return;
    }
    this.t += dtMs / 1000;
    const R = this.field.radius;
    const R2 = R * R;
    const midR2 = Math.pow(Math.min(MID_R[this.opts.tier], this.opts.midRadius), 2);

    // candidate list only when the camera moved a grid step
    const key = `${Math.floor(camX / 60)},${Math.floor(camZ / 60)}`;
    if (key !== this.candKey.k) {
      this.candKey.k = key;
      this.cand = this.grid.near(camX, camZ, R);
    }

    type Scored = { d2: number; c: Citizen; x: number; z: number; yaw: number };
    const vis: Scored[] = [];
    for (const ci of this.cand) {
      const c = this.citizens[ci];
      if (!c || !isOut(c, hourOfDay, day, rain)) continue;
      citizenPose(c, this.routes, this.t, this.pose);
      const dx = this.pose.x - camX;
      const dz = this.pose.z - camZ;
      const d2 = dx * dx + dz * dz;
      if (d2 > R2) continue;
      vis.push({ d2, c, x: this.pose.x, z: this.pose.z, yaw: this.pose.yaw });
    }
    vis.sort((a, b) => a.d2 - b.d2);

    this.field.begin(dtMs);
    let placed = 0;
    const cap = this.opts.visible;
    for (let i = 0; i < vis.length && placed < cap; i++) {
      const v = vis[i] as Scored & { look: CrowdLook };
      const lod = v.d2 <= midR2 ? 'mid' : 'far';
      const phase = (v.c.seed % 97) / 97;
      const ok = this.field.push(lod, v.c.look, v.x, 0, v.z, v.yaw, 'walk', phase, v.c.sp);
      if (!ok && lod === 'mid') {
        // mid pool full: overflow to far rather than dropping
        this.field.push('far', v.c.look, v.x, 0, v.z, v.yaw, 'walk', phase, v.c.sp);
      }
      placed++;
    }
    this.visibleCount = this.field.end();
  }

  stats(): { visible: number; sim: number; routes: number } {
    return { visible: this.visibleCount, sim: this.citizens.length, routes: this.routes.length };
  }
}

export { CitizenGrid };
